import { Timestamp, deleteField } from 'firebase/firestore';

/**
 * Firestore type of a lead attribute, from its leadField typeField. Twin of
 * functions/lib/leadFieldValue.js, which writes the same collection and picks
 * the same instants.
 *
 * Here it covers `date` only. The twin also covers `boolean` because over
 * there the values come from external webhooks, which do send empty strings;
 * the one boolean field here comes from a checkbox.
 *
 * The rule already existed, written by field name four times: `birthday` and
 * `customerAppointmentDate`, in createLeadSaga and in updateLeadSaga.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const FIRESTORE_TYPES = { date: 'Timestamp', boolean: 'Boolean' };

/**
 * Lives next to the rule that decides the type, not in the form: a label kept
 * away from its rule stops being true without anyone noticing.
 */
export const firestoreTypeOf = (typeField) =>
  FIRESTORE_TYPES[typeField] || 'String';

/**
 * A date is a calendar day, not an instant. The CRM used to anchor it to the
 * local midnight of whoever typed it — 546 of the last 723 on prod sit at
 * 23:00 UTC of the day before — and the web services format it inside a UTC
 * runtime, so it left one day early. Dates already stored are left alone.
 */
const utcMidnight = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC does not refuse the 31st of February: it answers the 3rd of
  // March. Shape-valid impossible dates reach here from the form and from
  // every webhook, and without reading the day back the lead would be saved
  // with a birthday nobody ever typed. null is what the caller already knows
  // how to handle.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return null;

  return Timestamp.fromDate(date);
};

/** null when the value is not a date: the caller decides what to do. */
const asTimestamp = (value) => {
  // leadTransformer converts birthday, customerAppointmentDate and createdAt;
  // `firstContact` reaches the store as a Timestamp, and treating it as
  // unreadable would delete it on every save.
  if (value instanceof Timestamp) return value;

  if (value instanceof Date && !isNaN(value.getTime()))
    return utcMidnight(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate(),
    );

  if (typeof value !== 'string' || value === '') return null;

  // Components come from the string: the form stores 'YYYY-MM-DD', which Date
  // reads as UTC midnight, and west of Greenwich its local getters would name
  // the previous day.
  const iso = ISO_DATE.exec(value);
  if (iso) return utcMidnight(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return utcMidnight(
    parsed.getFullYear(),
    parsed.getMonth() + 1,
    parsed.getDate(),
  );
};

/**
 * Only the keys whose value changes shape, to be merged over the raw ones.
 *
 * `deleteInvalid` tells the two writes apart. On update, a date field emptied
 * or filled in wrong has to go, which is what the saga already did. On create
 * it must not: addDocument rejects deleteField().
 */
export const coerceLeadValues = (
  values,
  leadFieldsByName,
  { deleteInvalid = true } = {},
) => {
  const changed = {};

  Object.keys(values || {}).forEach((name) => {
    const leadField = leadFieldsByName.get(name);
    if (!leadField || leadField.typeField !== 'date') return;

    const value = values[name];
    if (value === undefined || value === null) return;

    const timestamp = asTimestamp(value);
    if (timestamp === value) return;
    if (timestamp) changed[name] = timestamp;
    else if (deleteInvalid) changed[name] = deleteField();
  });

  return changed;
};
